import { Switch as BaseSwitch } from "@base-ui/react/switch";
import { useId, type ReactNode } from "react";
import { cx } from "./cx.js";

export interface SwitchProps {
  name?: string;
  label: ReactNode;
  description?: ReactNode;
  defaultChecked?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Base UI Switch styled as a Diotia control — the owned replacement for a
 * native checkbox. It always submits a value ("true" / "false"), so a form
 * action reads the setting whether it is on or off.
 */
export function Switch({ label, description, className, ...control }: SwitchProps) {
  const id = useId();
  return (
    <div className={cx("dt-switch", className)}>
      <span className="dt-switch__text">
        <label htmlFor={id} className="dt-field__label">
          {label}
        </label>
        {description && <span className="dt-field__desc">{description}</span>}
      </span>
      <SwitchControl id={id} {...control} />
    </div>
  );
}

/** The switch alone, for layouts that place the label themselves (settings rows): give it the label's `htmlFor` as `id`. */
export function SwitchControl({ id, name, defaultChecked, checked, onCheckedChange, disabled }: Omit<SwitchProps, "label" | "description" | "className"> & { id: string }) {
  return (
    <BaseSwitch.Root
      id={id}
      className="dt-switch__track"
      {...(name ? { name } : {})}
      value="true"
      uncheckedValue="false"
      {...(checked === undefined ? { defaultChecked: defaultChecked ?? false } : { checked })}
      {...(onCheckedChange ? { onCheckedChange: (next: boolean) => onCheckedChange(next) } : {})}
      {...(disabled ? { disabled } : {})}
    >
      <BaseSwitch.Thumb className="dt-switch__thumb" />
    </BaseSwitch.Root>
  );
}
